import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const InvoiceMaster = ({ invoiceArray, firmArray, items, taxBreakup, taxSummary }) => {
  // Get data from sessionStorage if props are not provided
  const [sessionData, setSessionData] = useState(null);
  
  useEffect(() => {
    // Check if we need to get data from sessionStorage
    if (!invoiceArray?.length && !sessionData) {
      try {
        const storedData = sessionStorage.getItem('invoiceData');
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setSessionData(parsedData);
          // Clear the session storage to avoid data persisting
          sessionStorage.removeItem('invoiceData');
        }
      } catch (error) {
        console.error('Error retrieving data from sessionStorage:', error);
      }
    }
  }, [invoiceArray, sessionData]);

  // Use props or session data
  const effectiveInvoiceArray = invoiceArray?.length ? invoiceArray : sessionData?.invoiceArray || [];
  const effectiveFirmArray = firmArray?.length ? firmArray : sessionData?.firmArray || [];
  const effectiveItems = items || sessionData?.items || [];
  const effectiveTaxBreakup = taxBreakup || sessionData?.taxBreakup || [];
  const effectiveTaxSummary = taxSummary || sessionData?.taxSummary || [];

  console.log(effectiveTaxBreakup, effectiveTaxSummary);
  
  // Use the first elements from the arrays if they exist
  const invoiceData = effectiveInvoiceArray?.[0] || {};
  const firmData = effectiveFirmArray?.[0] || {};

  // Check if we need pagination (more than 20 items)
  const needsPagination = effectiveItems.length > 20;

  // Data object derived from props or session data
  const data = {
    firm: {
      GST: firmData?.GST || '',
      FirmName: firmData?.FirmName || '',
      Address1: firmData?.Address1 || '',
      Address2: firmData?.Address2 || '',
      Mobile: firmData?.MobileNo || '',
      BankName: firmData?.BankName || '',
      AccountNo: firmData?.AccountNo || '',
      IFSC: firmData?.IFSC || '',
      MsmeNo: firmData?.MsmeNo || '',
    },
    buyer: {
      name: invoiceData?.PartyName || '',
      address: invoiceData?.Address1 || '',
      address2: invoiceData?.Address2 || '',
      gst: invoiceData?.GSTPO ? invoiceData.GSTPO.replace('GST No- ', '') : '',
      invoiceNo: invoiceData?.VoucherNo || '',
      invoiceDate: invoiceData?.Voucher_Date || '',
      challanNo: invoiceData?.ChallanNo || 'SEZ',
      challanDate: invoiceData?.Challan_Date || '',
      vehicle: invoiceData?.VehicleNo || '',
      poNo: invoiceData?.PONo || '',
      transport: invoiceData?.Transport || '',
      placeOfSupply: invoiceData?.PlaceOfSupply || '',
    },
    // Use effective items from props or session storage
    items: effectiveItems,
    // Use passed totals if available, otherwise generate default
    totals:  {
      taxable: invoiceData?.GrossAmount || 0,
      tax: invoiceData?.TaxationAmount || 0,
      cgst: invoiceData?.TaxationAmount ? invoiceData.TaxationAmount / 2 : 0,
      sgst: invoiceData?.TaxationAmount ? invoiceData.TaxationAmount / 2 : 0,
      roundOff: invoiceData?.RoundedValue || 0,
      grandTotal: invoiceData?.NetAmount || 0,
    },
    // Use effective tax data from props or session storage
    taxBreakup: effectiveTaxBreakup,
    taxSummary: effectiveTaxSummary
  };

  const tableHeaderStyle = {
    background: '#f0f0f0',
    fontWeight: 700,
    fontSize: 13,
    padding: '8px 5px',
    borderBottom: '2px solid #222',
    color: '#000',
  };
  
  const tableCellRight = { textAlign: 'right', color: '#000', padding: '6px 5px' };
  const tableCellCenter = { textAlign: 'center', color: '#000', padding: '6px 5px' };
  const tableCellLeft = { textAlign: 'left', color: '#000', padding: '6px 5px' };
  
  // Print-specific styles
  const printStyles = `
    @media print {
      /* Hide elements not meant for printing */
      .no-print {
        display: none !important;
      }
      
      /* Reset all margins and paddings */
      @page {
        size: A4;
        margin: 0 5mm !important;
      }
      
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        height: auto !important;
        color: black !important;
        -webkit-print-color-adjust: exact !important;
      }
      
      .page-content {
        padding: 0 !important;
        margin: 0 !important;
        background: white !important;
        color: black !important;
      }
      
      .print-container {
        border: none !important;
        box-shadow: none !important;
        padding: 7mm 10mm !important; /* Reduced from 10mm top padding to 7mm */
        margin: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        border-radius: 0 !important;
        background: white !important;
        color: black !important;
        transform: none !important;
        transform-origin: top left !important;
        box-sizing: border-box !important;
      }
      
      /* Handle pagination for the item table and post-items section */
      
      /* Make table headers repeat on each page */
      thead { 
        display: table-header-group !important;
      }
      
      /* Ensure the item rows can break across pages naturally */
      tr.item-row {
        page-break-inside: auto !important;
        line-height: 1.1 !important; /* Added line-height control */
      }
      
      /* For proper pagination of items */
      table { 
        page-break-inside: auto !important; 
        margin-top: 0 !important;  /* Remove top margin from tables */
        margin-bottom: 0 !important; /* Remove bottom margin from tables */
      }
      
      tr { 
        page-break-inside: avoid !important; 
      }
      
      /* Critical: Keep the post-items section together */
      .after-items-section {
        page-break-inside: avoid !important;
        padding-top: 5px !important; /* Reduced from 10px to 5px */
        margin-top: 8px !important; /* Reduced from 12px to 8px */
      }
      
      /* For proper spacing on the last page */
      .item-table-container {
        margin-bottom: ${needsPagination ? '0' : '3mm'} !important; /* Reduced from 5mm to 3mm */
        margin-top: 0 !important; /* Remove top margin */
      }
      
      /* Make table more compact when printing */
      table, tr, td, th {
        font-size: 11px !important;
        padding: 1px 3px !important; /* Reduced from 2px 3px to 1px 3px */
        line-height: 1.2 !important; /* Added line height control */
      }
      
      /* Adjust font sizes for printing */
      h1, h2, h3, h4, h5, h6 { margin: 2px 0 !important; }
      p, div { margin: 0 !important; line-height: 1.2 !important; }
      
      /* Make the header more compact */
      .invoice-header { margin-bottom: 3px !important; } /* Reduced from 5px to 3px */
      
      /* Adjust spacing between sections */
      .section-spacing { margin-top: 3px !important; margin-bottom: 3px !important; } /* Reduced from 5px to 3px */
      
      /* Preserve spacing for amount in words section */
      .amount-words-section { margin-top: 12px !important; margin-bottom: 5px !important; } /* Reduced from 25px to 12px */
      
      /* Horizontal lines */
      hr {
        margin: 5px 0 !important; /* Reduced margin */
      }
      
      /* Ensure background colors print */
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
        color: black !important;
      }
      
      /* Keep terms and conditions together */
      .terms-section {
        page-break-inside: avoid !important;
        margin-bottom: 5px !important; /* Added smaller margin */
      }
      
      .signature-section {
        page-break-inside: avoid !important;
        margin-top: 5px !important; /* Reduced from 10px to 5px */
      }
      
      /* Ensure signature space is maintained when printing, but smaller */
      .signature-name-row {
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
      }
      
      .signature-space {
        height: 15px !important; /* Reduced from 25px to 15px */
        min-height: 15px !important;
        display: block !important;
        visibility: visible !important;
      }
      
      .signature-title-row {
        margin-top: 0 !important;
        padding-top: 0 !important;
      }
      
      /* All table cells more compact */
      td, th {
        line-height: 1.2 !important;
      }
      
      /* More compact list */
      ol, ul {
        padding-left: 15px !important; /* Reduced from 20px */
        margin: 0 !important;
      }
      
      li {
        margin-bottom: 0 !important;
        line-height: 1.2 !important;
      }
    }
  `;

  // Function to handle printing
  const handlePrint = () => {
    window.print();
  };
  
  // Added state for copy type
  const [copyType, setCopyType] = useState("ORIGINAL");

  // Helper function to safely format numbers
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '';
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '' : numValue.toFixed(2);
  };

  // Create a table header component that can be reused for each page
  const ItemTableHeader = () => (
    <thead>
      <tr>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>SN</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Code</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Description</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Ply</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Calc</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Qty</th>
        {/* <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Rate</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Amount</th> */}
      </tr>
    </thead>
  );

  // Adjust signature space
  const signature = (
    <div className="signature-section" style={{ width: '30%', fontSize: 12, color: '#000' }}>
      <div style={{ textAlign: 'right', fontWeight: 600 }}>
        <div className="signature-name-row">For <b>{data.firm.FirmName}</b></div>
        {/* This is a spacer specifically for signatures */}
        <div className="signature-space" style={{ height: 20, border: '0px dashed transparent' }}></div>
        <div className="signature-title-row">Authorized Signatory</div>
      </div>
    </div>
  );

  return (
    <div className="page-content">
      {/* Add print-specific styles */}
      <style>{printStyles}</style>
      
      <div
        className="print-container"
        style={{
          fontFamily: 'Segoe UI, Arial, sans-serif',
          fontSize: 13,
          maxWidth: 900,
          margin: '0 auto',
          background: '#fff',
          border: '1.5px solid #222',
          borderRadius: 8,
          boxShadow: '0 3px 15px rgba(0,0,0,0.15)',
          padding: '20px 35px', /* Reduced from 25px to 20px top/bottom padding */
          color: '#000',
          lineHeight: 1.2
        }}
      >
        {/* Header and company name section */}
        <div className="invoice-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, lineHeight: 1.2 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#000' }}>
              <span style={{ fontWeight: 600 }}>GSTIN:</span> {data.firm.GST}
            </div>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: 1, marginBottom: 2, color: '#000', textTransform: 'uppercase' }}>Challan</div>
            <div style={{ fontWeight: 800, fontSize: 24, letterSpacing: 1, marginBottom: 2, color: '#000' }}>{data.firm.FirmName}</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#000' }}>{data.firm.Address1}</div>
            <div style={{ fontWeight: 500, fontSize: 13, color: '#000' }}>{data.firm.Address2}</div>
            <div style={{ fontWeight: 500, fontSize: 12, marginTop: 1, color: '#000' }}>{data.firm.Mobile}</div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, textAlign: 'right', color: '#000' }}>
            <div>Mobile: {data.firm.Mobile}</div>
            <div style={{ marginTop: 6, fontSize: 14, fontWeight: 700, color: '#000' }}>{copyType}</div>
          </div>
        </div>
        <hr style={{ border: 'none', borderTop: '2px solid #222', margin: '5px 0' }} />

        {/* Buyer/Consignee/Invoice Section - Restructured with reduced spacing */}
        <div className="section-spacing" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: '#000' }}>
          {/* BUYER - Left */}
          <div style={{ width: '30%' }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2, color: '#000' }}>BUYER</div>
            <div style={{ fontWeight: 600, color: '#000' }}>M/s : {data.buyer.name}</div>
            <div style={{ color: '#000', fontSize: 12 }}>{data.buyer.address}</div>
            <div style={{ color: '#000', fontSize: 12 }}>{data.buyer.address2}</div>
            <div style={{ marginTop: 2, fontWeight: 600, fontSize: 11, color: '#000' }}>
              GST No- {data.buyer.gst}
            </div>
          </div>
          
          {/* Consignee - Center */}
          <div style={{ width: '35%', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#000' }}>Consignee : {"  "} </div>
              <div style={{ marginLeft: 7, fontSize: 13, color: '#000' }}>{invoiceData?.ConsigneeAddress1 || ''}</div>
            </div>
            {invoiceData?.ConsigneeAddress2 && <div style={{ fontSize: 12 }}>{invoiceData.ConsigneeAddress2}</div>}
            {invoiceData?.ConsigneeAddress3 && <div style={{ fontSize: 12 }}>{invoiceData.ConsigneeAddress3}</div>}
          </div>
          
          {/* Invoice Details - Right */}
          <div style={{ width: '30%' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <tbody>
             
                <tr>
                  <td style={{ fontWeight: 600, padding: '2px', color: '#000' }}>Challan No.</td>
                  <td style={{ padding: '2px', textAlign: 'center' }}>:</td>
                  <td style={{ textAlign: 'right', padding: '2px' }}>{data.buyer.challanNo}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '2px', color: '#000' }}>Challan Date</td>
                  <td style={{ padding: '2px', textAlign: 'center' }}>:</td>
                  <td style={{ textAlign: 'right', padding: '2px' }}>{data.buyer.challanDate}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '2px', color: '#000' }}>VEHICLES</td>
                  <td style={{ padding: '2px', textAlign: 'center' }}>:</td>
                  <td style={{ textAlign: 'right', padding: '2px' }}>{data.buyer.vehicle}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '2px', color: '#000' }}>P.O. No.</td>
                  <td style={{ padding: '2px', textAlign: 'center' }}>:</td>
                  <td style={{ textAlign: 'right', padding: '2px' }}>{data.buyer.poNo}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '2px', color: '#000' }}>Place of Supply</td>
                  <td style={{ padding: '2px', textAlign: 'center' }}>:</td>
                  <td style={{ textAlign: 'right', padding: '2px' }}>{data.buyer.placeOfSupply}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Item Table with reduced spacing */}
        <div className="item-table-container">
          <table className="section-spacing" style={{ width: '100%', borderCollapse: 'collapse', margin: '5px 0 0 0', fontSize: 12, border: '1px solid #ddd' }}>
            <ItemTableHeader />
            <tbody>
              {Array.isArray(data.items) && data.items.map((item, index) => {
                // Skip if item is null or undefined
                if (!item) return null;
                
                // First row is the header row
                if (index === 0 && item.Description1) {
                  return (
                    <tr key={index}>
                      <td colSpan="8" style={{ fontWeight: 600, background: '#f5f5f5', fontSize: 12, padding: '4px 3px', color: '#000', borderBottom: '1px solid #ddd' }}>
                        {item.Description1}
                      </td>
                    </tr>
                  );
                }
                
                // Check if row is empty
                const isEmptyRow = !item.Counter && !item.Description1 && !item.CodeNo && 
                                  !item.Description2 && !item.Description3 && 
                                  !item.Qty && !item.Rate && !item.Amount;
                
                // Instead of skipping empty rows, we'll render them with no border and reduced height
                return (
                  <tr className="item-row" key={index} style={{ 
                    borderBottom: isEmptyRow ? 'none' : '1px solid #eee',
                    backgroundColor: isEmptyRow ? 'transparent' : (index % 2 === 0 ? '#fff' : '#fafafa'),
                    height: isEmptyRow ? '25px' : 'auto',  /* Reduced from 8px to 6px for empty rows */
                    lineHeight: '1.4'
                  }}>
                    <td style={{...tableCellCenter, fontSize: 13, padding: isEmptyRow ? '0px' : '2px'}}>{item.Counter || ''}</td>
                    <td style={{...tableCellLeft, fontSize: 13, padding: isEmptyRow ? '0px' : '2px',textAlign:'left'}}>{item.CodeNo || ''}</td>
                    <td style={{...tableCellCenter, fontSize: 13, padding: isEmptyRow ? '0px' : '2px',textAlign:'left'}}>{item.Description1 || ''}</td>
                    <td style={{...tableCellCenter, fontSize: 13, padding: isEmptyRow ? '0px' : '2px',textAlign:'left'}}>{item.Description2 || ''}</td>
                    <td style={{...tableCellCenter, fontSize: 13, padding: isEmptyRow ? '0px' : '2px',textAlign:'left'}}>{item.Description3 || ''}</td>
                    <td style={{...tableCellRight, fontSize: 13, padding: isEmptyRow ? '0px' : '2px'}}>{item.Qty || ''}</td>
                    {/* <td style={{...tableCellRight, fontSize: 13, padding: isEmptyRow ? '0px' : '2px'}}>{item.Rate ? formatNumber(item.Rate) : ''}</td>
                    <td style={{...tableCellRight, fontSize: 13, padding: isEmptyRow ? '0px' : '2px'}}>{item.Amount ? formatNumber(item.Amount) : ''}</td> */}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Post-Items Section with reduced spacing */}
        <div className="after-items-section section-spacing" style={{ marginTop: 8, paddingTop: 5 }}>
          {/* Tax Summary Table with Bank Details in leftmost column */}
          {/* <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: '3px' }}>
            <tbody>
              <tr>
                <td width="50%" rowSpan="3" style={{ verticalAlign: 'top', paddingRight: '8px', borderRight: '1px solid #ddd' }}>
                  <div style={{ fontWeight: 700, marginBottom: 1, lineHeight: 1.2 }}>Our Bank:</div>
                  <div style={{ lineHeight: 1.2 }}>{data.firm.BankName}</div>
                  <div style={{ lineHeight: 1.2 }}>{data.firm.AccountNo}</div>
                  <div style={{ lineHeight: 1.2 }}>{data.firm.IFSC}</div>
                </td>
                <td width="15%" style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600, background: '#f5f5f5', color: '#000', fontSize: 11, textAlign: 'left' }}>
                  {data.taxSummary?.[0]?.Name || ''}
                </td>
                <td width="10%" style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', color: '#000', fontSize: 11 }}>
                  {data.taxSummary?.[0]?.Amount ? formatNumber(data.taxSummary[0].Amount) : '0.00'}
                </td>
                <td width="15%" style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600, background: '#f5f5f5', color: '#000', fontSize: 11, textAlign: 'left' }}>Total</td>
                <td width="10%" style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', color: '#000', fontSize: 11 }}>
                  {data.taxSummary?.[0]?.Amount ? formatNumber(data.taxSummary[0].Amount) : '0.00'}
                </td>
              </tr>
              <tr>
                <td style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600, color: '#000', fontSize: 11, textAlign: 'left' }}>
                  {data.taxSummary?.[1]?.Name || ''}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', color: '#000', fontSize: 11 }}>
                  {data.taxSummary?.[1]?.Amount ? formatNumber(data.taxSummary[1].Amount) : '0.00'}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600, color: '#000', fontSize: 11, textAlign: 'left' }}>
                  {data.taxBreakup?.[0]?.TaxName || ''}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', color: '#000', fontSize: 11 }}>
                  {data.taxBreakup?.[0]?.AddAmt ? formatNumber(data.taxBreakup[0].AddAmt) : '0.00'}
                </td>
              </tr>
              <tr>
                <td colSpan="2" style={{ borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd' }}></td>
                <td style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600, color: '#000', fontSize: 11, textAlign: 'left' }}>
                  {data.taxBreakup?.[1]?.TaxName || ''}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', color: '#000', fontSize: 11 }}>
                  {data.taxBreakup?.[1]?.AddAmt ? formatNumber(data.taxBreakup[1].AddAmt) : '0.00'}
                </td>
              </tr>
            </tbody>
          </table> */}

          {/* Round Off Row with reduced spacing */}
    
          {/* Amount in words with reduced spacing */}
       
          
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 3 }}>
            <tbody>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                {/* MSME moved to left side */}
                <td width="75%" style={{ textAlign: 'left', padding: '4px', color: '#000', fontSize: 11 }}>Unit Registered Under MSME Act. No.: <strong>{data.firm.MsmeNo}</strong></td>
                <td style={{ textAlign: 'right', padding: '4px', fontWeight: 700, fontSize: 13, color: '#000', whiteSpace: 'nowrap' }}>
                  {/* Grand Total: */}
                  </td>
                <td style={{ textAlign: 'right', padding: '4px', fontWeight: 700, fontSize: 13, color: '#000', width: '25%' }}>
                  {/* {data.totals?.grandTotal !== undefined && data.totals.grandTotal !== null ? formatNumber(data.totals.grandTotal) : '0.00'} */}
                </td>
              </tr>
            </tbody>
          </table>

          <hr style={{ border: 'none', borderTop: '2px solid #222', margin: '8px 0 5px 0' }} />

          {/* Amount in words - Added both GrossAmount and TaxationAmount with reduced spacing */}
          <div className="amount-words-section" style={{ fontSize: 11, marginBottom: 5, color: '#000', marginTop: 10, lineHeight: 1.2 }}>
            <div style={{ marginBottom: 2 }}>
              <span style={{ fontWeight: 600 }}>
                {/* Gross Amount in words:  */}
                </span>
              <span style={{ fontStyle: 'italic' }}>
                {/* {invoiceData?.GrossAmountInWords || ''} */}
                </span>
            </div>
            <div>
              <span style={{ fontWeight: 600 }}>
                {/* Tax Amount in words:  */}
                </span>
              <span style={{ fontStyle: 'italic' }}>
                {/* {invoiceData?.TaxationAmountInWords || ''} */}
                </span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            {/* Terms and Conditions section with reduced spacing */}
            <div className="terms-section" style={{ fontSize: 11, color: '#000', width: '60%', lineHeight: 1.2 }}>
              <div style={{ fontWeight: 700, marginBottom: 2, color: '#000' }}>Terms & Conditions :</div>
              <ol style={{ paddingLeft: 15, margin: 0 }}>
                <li style={{ marginBottom: 0, lineHeight: 1.2 }}>Our Firm Registered in MSME.</li>
                <li style={{ marginBottom: 0, lineHeight: 1.2 }}>Subject to Jodhpur Jurisdiction only.</li>
                <li style={{ marginBottom: 0, lineHeight: 1.2 }}>No Claim will be entertained after dispatch of Goods.</li>
                <li style={{ lineHeight: 1.2 }}>E & O.E..</li>
              </ol>
            </div>
            
            {/* Signature section with reduced spacing */}
            <div className="signature-section" style={{ width: '30%', fontSize: 12, color: '#000' }}>
              <div style={{ textAlign: 'right', fontWeight: 600, lineHeight: 1.2 }}>
                <div className="signature-name-row">For <b>{data.firm.FirmName}</b></div>
                {/* This is a spacer specifically for signatures */}
                <div className="signature-space" style={{ height: 15, border: '0px dashed transparent' }}></div>
                <div className="signature-title-row">Authorized Signatory</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Print Button - will not show when printing */}
      <div className="no-print" style={{ textAlign: 'center', marginTop: 20, marginBottom: 40 }}>
        <div style={{ marginBottom: 15 }}>
          <select 
            value={copyType}
            onChange={(e) => setCopyType(e.target.value)}
            style={{
              padding: '8px 15px',
              borderRadius: 4,
              border: '1px solid #ccc',
              marginRight: 10,
              fontSize: 14
            }}
          >
            <option value="ORIGINAL">ORIGINAL</option>
            <option value="DUPLICATE">DUPLICATE</option>
            <option value="TRIPLICATE">TRIPLICATE</option>
          </select>
          <span style={{ fontSize: 14 }}>Select copy type</span>
        </div>
        
        <button
          onClick={handlePrint}
          style={{
            backgroundColor: '#2a41cc',
            color: 'white',
            border: 'none',
            borderRadius: 5,
            padding: '12px 30px',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
        >
          Print Invoice
        </button>
      </div>
    </div>
  );
};

// Define the prop types for validation
InvoiceMaster.propTypes = {
  invoiceArray: PropTypes.arrayOf(PropTypes.shape({
    VoucherNo: PropTypes.string,
    Voucher_Date: PropTypes.string,
    ChallanNo: PropTypes.string,
    PartyName: PropTypes.string,
    GSTPO: PropTypes.string,
    Address2: PropTypes.string,
    GrossAmount: PropTypes.number,
    TaxationAmount: PropTypes.number,
    NetAmount: PropTypes.number,
    PONo: PropTypes.string,
    VehicleNo: PropTypes.string,
    GrossAmountInWords: PropTypes.string,
    TaxationAmountInWords: PropTypes.string,
    NetAmountInWords: PropTypes.string,
    ConsigneeAddress1: PropTypes.string,
    ConsigneeAddress2: PropTypes.string,
    ConsigneeAddress3: PropTypes.string,
    RoundedValue: PropTypes.number,
    PlaceOfSupply: PropTypes.string
  })),
  firmArray: PropTypes.arrayOf(PropTypes.shape({
    GST: PropTypes.string,
    FirmName: PropTypes.string,
    Address1: PropTypes.string,
    Address2: PropTypes.string,
    MobileNo: PropTypes.string,
    BankName: PropTypes.string,
    AccountNo: PropTypes.string,
    IFSC: PropTypes.string,
    MsmeNo: PropTypes.string
  })),
  items: PropTypes.arrayOf(PropTypes.shape({
    Counter: PropTypes.number,
    SNo: PropTypes.number,
    Id: PropTypes.string,
    CodeNo: PropTypes.string,
    Description1: PropTypes.string,
    Description2: PropTypes.string,
    Description3: PropTypes.string,
    HSNCode: PropTypes.string,
    Qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ItemUnit: PropTypes.string,
    Amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ItemType: PropTypes.string,
    Tax: PropTypes.string
  })),
  taxBreakup: PropTypes.arrayOf(PropTypes.shape({
    TaxName: PropTypes.string,
    AddAmt: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  taxSummary: PropTypes.arrayOf(PropTypes.shape({
    Name: PropTypes.string,
    Amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  totals: PropTypes.shape({
    taxable: PropTypes.number,
    tax: PropTypes.number,
    cgst: PropTypes.number,
    sgst: PropTypes.number,
    roundOff: PropTypes.number,
    grandTotal: PropTypes.number
  })
};

// Define default props
InvoiceMaster.defaultProps = {
  invoiceArray: [],
  firmArray: [],
  items: null, // null will make the component use the default items in the data object
  taxBreakup: null,
  taxSummary: null,
  totals: null
};

export default InvoiceMaster;
